import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { highlight, languageLabel } from '../lib/highlight';

/**
 * Lesson text, presented.
 *
 * The HTML arrives sanitised from the API (headings, lists, tables, code,
 * links). This adds what a reader expects of a professional course page and
 * HTML alone cannot give:
 *
 *  - Code and JSON in an editor-style panel: a language label, syntax
 *    colouring, and a Copy button. Colouring works on the text content and
 *    escapes everything it emits, so it cannot reintroduce markup.
 *  - Wide tables scroll inside their own frame on a phone, instead of
 *    widening the whole page.
 *  - A lesson's own "Contents" (a heading followed by a list of links) is
 *    shown as a card, and its links scroll within the page.
 *  - Links to other lessons ("/learn/…") move inside the app rather than
 *    reloading it.
 *
 * Enhancement happens after render, on the DOM React wrote from the HTML;
 * when the HTML changes, React rewrites the container and this runs again.
 */
export function RichText({ html, className = '' }: { html: string; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    // ---- the lesson's own contents: "Contents" heading + list → card
    const first = root.firstElementChild;
    if (first?.tagName === 'H2' && /^contents$/i.test(first.textContent?.trim() ?? '')) {
      const list = first.nextElementSibling;
      if (list && (list.tagName === 'UL' || list.tagName === 'OL')) {
        const nav = document.createElement('nav');
        nav.className = 'prose__toc';
        nav.setAttribute('aria-label', 'In this lesson');
        const label = document.createElement('p');
        label.className = 'prose__toc-label';
        label.textContent = 'In this lesson';
        root.insertBefore(nav, first);
        nav.append(label, list);
        first.remove();
        const rule = nav.nextElementSibling;
        if (rule?.tagName === 'HR') rule.remove();
      }
    }

    // ---- a course contents: a list whose every item is one lesson link
    root.querySelectorAll('ol, ul').forEach((list) => {
      const items = [...list.children];
      const isSyllabus =
        items.length >= 3 &&
        items.every((item) => {
          const link = item.querySelector(':scope > a');
          return link && item.textContent?.trim() === link.textContent?.trim() && link.getAttribute('href')?.startsWith('/learn/');
        });
      if (isSyllabus) list.classList.add('prose__syllabus');
    });

    // ---- code panels
    root.querySelectorAll('pre').forEach((pre) => {
      if (pre.parentElement?.classList.contains('codepanel')) return;
      const code = pre.querySelector('code') ?? pre;
      const match = /language-([\w-]+)/.exec(`${code.className} ${pre.className}`);
      const language = match?.[1] ?? 'text';
      const source = code.textContent ?? '';
      code.innerHTML = highlight(source, language);

      const panel = document.createElement('figure');
      panel.className = 'codepanel';
      const bar = document.createElement('figcaption');
      bar.className = 'codepanel__bar';
      const dots = document.createElement('span');
      dots.className = 'codepanel__dots';
      dots.setAttribute('aria-hidden', 'true');
      dots.innerHTML = '<i></i><i></i><i></i>';
      const name = document.createElement('span');
      name.className = 'codepanel__lang';
      name.textContent = languageLabel(language);
      const copy = document.createElement('button');
      copy.type = 'button';
      copy.className = 'codepanel__copy';
      copy.textContent = 'Copy';
      copy.setAttribute('aria-label', `Copy ${languageLabel(language)} code`);
      copy.addEventListener('click', () => {
        const done = (label: string) => {
          copy.textContent = label;
          window.setTimeout(() => (copy.textContent = 'Copy'), 1800);
        };
        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(source).then(
            () => done('Copied'),
            () => done('Copy failed'),
          );
        } else {
          done('Copy failed');
        }
      });
      bar.append(dots, name, copy);
      pre.replaceWith(panel);
      panel.append(bar, pre);
      // Scrollable regions must be reachable by keyboard.
      pre.tabIndex = 0;
      pre.setAttribute('aria-label', `${languageLabel(language)} code`);
    });

    // ---- tables: scroll inside a frame on narrow screens
    root.querySelectorAll('table').forEach((table) => {
      if (table.parentElement?.classList.contains('prose__table')) return;
      const frame = document.createElement('div');
      frame.className = 'prose__table';
      frame.tabIndex = 0;
      frame.setAttribute('role', 'region');
      frame.setAttribute('aria-label', 'Table');
      table.replaceWith(frame);
      frame.append(table);
    });

    // ---- labelled paragraphs ("Goal for the day: …") → callout cards
    root.querySelectorAll(':scope > p').forEach((paragraph) => {
      const lead = paragraph.firstChild;
      if (!(lead instanceof HTMLElement) || lead.tagName !== 'STRONG') return;
      const label = lead.textContent?.trim() ?? '';
      if (!/:$/.test(label) || label.length > 40) return;
      const variant = calloutVariant(label);
      const card = document.createElement('div');
      card.className = `callout callout--${variant}`;
      const icon = document.createElement('span');
      icon.className = 'callout__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = CALLOUT_ICONS[variant];
      const body = document.createElement('div');
      body.className = 'callout__body';
      const title = document.createElement('p');
      title.className = 'callout__label';
      title.textContent = label.replace(/:$/, '');
      lead.remove();
      const text = document.createElement('p');
      text.className = 'callout__text';
      text.append(...paragraph.childNodes);
      body.append(title, text);
      card.append(icon, body);
      paragraph.replaceWith(card);
    });

    // ---- section-aware styling: what a heading introduces
    root.querySelectorAll(':scope > h2').forEach((heading) => {
      const text = heading.textContent?.trim() ?? '';
      let next = heading.nextElementSibling;
      // Questions to check understanding → question cards.
      if (/check (yourself|your understanding)|self[- ]check|review questions|questions/i.test(text)) {
        if (next && (next.tagName === 'UL' || next.tagName === 'OL')) next.classList.add('prose__questions');
      }
      // Mistakes and troubleshooting tables → warning accent.
      if (/mistake|troubleshoot|pitfall|problem/i.test(text)) {
        while (next && next.tagName !== 'H2') {
          if (next.classList.contains('prose__table')) next.classList.add('prose__table--warn');
          next = next.nextElementSibling;
        }
      }
    });
    // Numbered steps (not the contents or a syllabus) → a step timeline.
    root.querySelectorAll(':scope > ol:not(.prose__syllabus)').forEach((list) => list.classList.add('prose__steps'));
    // Sections are numbered when a lesson has enough of them to navigate by.
    if (root.querySelectorAll(':scope > h2').length >= 3) root.classList.add('prose--numbered');
    // Column widths from content: a column whose cells are all short (a week
    // number, "Day 3 to 5", "20 min") stays narrow and on one line; columns
    // of sentences share the rest. A fixed rule cannot suit every table.
    root.querySelectorAll('.prose__table table').forEach((table) => {
      const rows = [...table.querySelectorAll('tr')];
      const columns = Math.max(0, ...rows.map((row) => row.children.length));
      for (let c = 0; c < columns; c++) {
        const cells = rows.map((row) => row.children[c]).filter((cell): cell is Element => !!cell);
        const longest = Math.max(0, ...cells.map((cell) => (cell.textContent ?? '').trim().length));
        if (longest > 0 && longest <= 14) cells.forEach((cell) => cell.classList.add('is-tight'));
      }
    });
    // A timetable: a "Minutes" column becomes time chips.
    root.querySelectorAll('.prose__table table').forEach((table) => {
      const head = table.querySelector('thead th');
      if (!/^(minutes|mins?|time|duration)$/i.test(head?.textContent?.trim() ?? '')) return;
      table.classList.add('is-schedule');
      table.querySelectorAll('tbody tr').forEach((row) => {
        const cell = row.firstElementChild;
        const value = cell?.textContent?.trim() ?? '';
        if (cell && /^\d{1,3}$/.test(value)) {
          cell.innerHTML = '';
          const chip = document.createElement('span');
          chip.className = 'timechip';
          chip.textContent = `${value} min`;
          cell.append(chip);
        }
      });
    });

    // ---- links: in-page anchors scroll; lesson links navigate in the app
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) {
        return;
      }
      const anchor = (event.target as HTMLElement).closest('a');
      const href = anchor?.getAttribute('href');
      if (!anchor || !href) return;
      if (href.startsWith('#')) {
        const target = root.querySelector(`[id="${CSS.escape(href.slice(1))}"]`);
        if (target) {
          event.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          (target as HTMLElement).setAttribute('tabindex', '-1');
          (target as HTMLElement).focus({ preventScroll: true });
        }
      } else if (href.startsWith('/') && !href.startsWith('//')) {
        event.preventDefault();
        navigate(href);
      } else if (/^https?:/i.test(href)) {
        // Outside links open beside the course, not instead of it.
        anchor.setAttribute('target', '_blank');
      }
    };
    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [html, navigate]);

  return (
    <div
      ref={ref}
      className={`prose prose--lesson ${className}`.trim()}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

type CalloutVariant = 'goal' | 'info' | 'deliver' | 'task' | 'stretch' | 'warn' | 'note';

const CALLOUT_ICONS: Record<CalloutVariant, string> = {
  goal: '\u{1F3AF}',
  info: 'ℹ',
  deliver: '✓',
  task: '✎',
  stretch: '★',
  warn: '!',
  note: '•',
};

/** Which kind of callout a label introduces, by what it says. */
function calloutVariant(label: string): CalloutVariant {
  const text = label.toLowerCase();
  if (/goal|objective|outcome|aim/.test(text)) return 'goal';
  if (/hand in|deliver|done when|submit|output|you produce/.test(text)) return 'deliver';
  if (/stretch|bonus|extra|challenge/.test(text)) return 'stretch';
  if (/warning|caution|important|avoid|don.t/.test(text)) return 'warn';
  if (/task|exercise|activity|try|practice/.test(text)) return 'task';
  if (/ai|service|note|tip|info|requirement|prerequisite|tool/.test(text)) return 'info';
  return 'note';
}
